import { pick } from 'es-toolkit';
import { Middleware } from 'redux';
import { isAction } from '@reduxjs/toolkit';

import { projectSynchronized } from '@paragon/layout-models';
import { enqueuePermanentError } from '@paragon/notification-tools';
import { isRemoteAction } from '@paragon/sync-client';

import { RootState } from '..';
import { ReduxDispatch } from '../actions/thunkActions';
import { saveActions } from '../slices/componentDesignsSlice';
import { removeJackGirderConnections, setJackGirderConnections } from '../slices/jackGirderConnectionsSlice';
import { unsynchronizedChangeMade } from '../synchronizationActions';

const relevantLocalActions = new Set<string>(
  saveActions
    .map<string>((action) => action.type)
    .concat([setJackGirderConnections, removeJackGirderConnections].map((action) => action.type)),
);

export const syncWatchdogMiddleware: Middleware<{}, RootState, ReduxDispatch> = (store) => (next) => (action) => {
  if (!isAction(action)) {
    return next(action);
  }

  const state = store.getState();

  if (state.application.openContext?.type === 'project') {
    const { projectGuid } = state.application.openContext;

    if (
      isRemoteAction(action) &&
      unsynchronizedChangeMade.match(action) &&
      projectGuid === action.payload.projectGuid
    ) {
      store.dispatch(
        enqueuePermanentError(
          'One or more component designs have been modified in a different window.  Please reload the design client.',
        ),
      );
    } else if (action.type && relevantLocalActions.has(action.type)) {
      store.dispatch(unsynchronizedChangeMade({ projectGuid }));
    }

    if (isRemoteAction(action) && projectSynchronized.match(action)) {
      const filteredPayload = pick(action.payload, [projectGuid]);
      const filteredAction = {
        ...action,
        payload: filteredPayload,
      };

      return next(filteredAction);
    }
  }

  return next(action);
};
